import { createContext, useReducer, useContext } from 'react'
import { CenteredSpinner } from 'react-angle-dashboard-components'

import { Actions, GbsAction } from './actions'
import { GbsState, GbsStep } from './interfaces'
import { Contribution } from '../interfaces/contribution'
import { useInit } from '../hooks/use-init'

interface GbsContext {
  state: GbsState
  dispatch: React.Dispatch<GbsAction>
}

export const GbsStore = createContext<GbsContext>({
  dispatch: () => {},
  state: {
    currentStepKey: 'recommendedPlans',
    steps: { recommendedPlans: null },
    initialQuoteInformation: null,
    zipCode: ''
  }
})

export const useGbsContext = () => useContext(GbsStore)

const gbsReducer = (state: GbsState, action: GbsAction): GbsState => {
  switch (action.type) {
    case Actions.InitializeStep: {
      const key = action.payload.stepKey as keyof typeof state.steps
      if (!key)
        throw new Error(
          '[InitializeStep] You must provide the stepKey in the payload'
        )

      return {
        ...state,
        steps: {
          ...state.steps,
          [key]: {
            ...action.payload.stepConfig,
            data: { ...state.steps[key]?.data }
          }
        }
      }
    }

    case Actions.SetCurrentStep: {
      const key = action.payload as keyof typeof state.steps
      if (!key)
        throw new Error(
          '[SetCurrentStep] You must provide the stepKey in the payload'
        )

      if (!state.steps[key])
        throw new Error(
          `[SetCurrentStep] Step ${key} not found, initialize it first`
        )

      return {
        ...state,
        currentStepKey: key
      }
    }

    case Actions.SetStepData: {
      const key = action.payload.stepKey as keyof typeof state.steps
      if (!key)
        throw new Error(
          '[SetStepData] You must provide the stepKey in the payload'
        )

      let newState = { ...state }

      if (!newState.steps[key]) {
        newState.steps[key] = {}
      }

      return {
        ...newState,
        steps: {
          ...newState.steps,
          [key]: {
            ...(newState.steps[key] as GbsStep),
            data: { ...newState.steps[key]?.data, ...action.payload.stepData }
          }
        }
      }
    }

    case Actions.SetInitialQuoteInformation: {
      return {
        ...state,
        initialQuoteInformation: {
          ...state.initialQuoteInformation,
          ...action.payload
        }
      }
    }

    case Actions.SetBottomBarEdit: {
      return {
        ...state,
        isEditingBottomBar: action.payload
      }
    }

    case Actions.SetZipCode: {
      return {
        ...state,
        zipCode: action.payload
      }
    }

    default:
      throw new Error('Invalid action')
  }
}

export const GbsProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(gbsReducer, {
    steps: {
      recommendedPlans: { data: { avgAge: 35 } },
      contribution: {
        data: {
          value: 50,
          type: Contribution.PERCENTAGE,
          dependentContribution: 50
        }
      }
    },
    initialQuoteInformation: null,
    currentStepKey: 'recommendedPlans',
    zipCode: ''
  })

  const { isLoading } = useInit(dispatch, state.zipCode)

  if (isLoading) {
    return (
      <div className="vh-100 vw-100">
        <CenteredSpinner />
      </div>
    )
  }

  return (
    <GbsStore.Provider value={{ state, dispatch }}>
      {children}
    </GbsStore.Provider>
  )
}
